import React, { PureComponent } from "react";
import ReactEcharts from "echarts-for-react";

import { Chart, ChartProps, ColorProps, GlobalColorPalette, ChartStyleProps, ChartHighLight, ChartHighLightProps } from "@gago/frame";
import tinycolor from "tinycolor2";
import styled from "styled-components";

// tslint:disable:variable-name jsx-no-multiline-js
// tslint:disable:completed-docs
const ChartBox = styled.div<{chartStyle: ChartStyleProps["chartStyle"]}>`
  height: 100%;
  width:100%;
  overflow: hidden;
  position: relative;
  .left-ytable{
    position: absolute;
    top: 0;
    left: 0;
    font-size: ${props => props.chartStyle.secondaryTextSize}px;
    color: ${props => props.chartStyle.secondaryTextColor};
  }
  .right-ytable{
    left: unset;
    right: 0px;
  }
  .legend-box{
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: flex-start;
    align-items: center;
    .legend-single{
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-right: 12px;
      &:last-of-type{
        margin-right: 0px;
      }
      .ls-icon{
        height: 8px;
        width: 8px;
        border-radius: 50%;
        background-color: transparent;
      }
      .ls-label{
        margin-left: 6px;
        font-size: ${props => props.chartStyle.secondaryTextSize}px;
        color: ${props => props.chartStyle.secondaryTextColor};
      }
    }
  }
`;

export type BarChartProps = {
  /** 柱状的宽度 */
  barWidth: number;
  /** 柱的间距 */
  barGap: number;
} & ChartProps<1, 2> & ColorProps<GlobalColorPalette> & ChartStyleProps & ChartHighLightProps;

/**
 * 双Y轴柱状图
 * @author 马俊峰
 * @date 2020-04-26
 * @export
 * @class DoubleBarChart
 * @extends {PureComponent<BarChartProps>}
 * @implements {Chart<1, 2>}
 */
export class DoubleBarChart extends PureComponent<BarChartProps> implements Chart<1, 2>, ChartHighLight {
  static defaultProps = {
    barWidth: 16,
    barGap: 0.3,
    chartHighLight: undefined,
    onChartClick() {},
    onChartHover() {},
  };
  render() {
    const { chartData: { data, head, suffix }, chartStyle, colorPalette: { subColor } } = this.props;
    const xAxis: string[] = [];
    const yAxis1: number[] = [];
    const yAxis2: number[] = [];
    for (const item of data) {
      xAxis.push(item[1]);
      yAxis1.push(item[2]);
      yAxis2.push(item[3]);
    }
    const legendRange = head.slice(-2);
    const option = this.getBarChartOption(xAxis, yAxis1, yAxis2);
    return (
      <ChartBox chartStyle={chartStyle}>
        <span className="left-ytable">{head[2]}：{suffix && suffix[2]}</span>
        <span className="left-ytable right-ytable">{head[3]}：{suffix && suffix[3]}</span>
        <div className="legend-box">
          {legendRange.map((item, index) => (
            <div className="legend-single" key={index}>
              <span className="ls-icon" style={{ backgroundColor: subColor[index] }} />
              <span className="ls-label">{item}</span>
            </div>
          ))}
        </div>
        <ReactEcharts notMerge style={{ height: "100%", width: "100%" }} option={option} />
      </ChartBox>
    );
  }
  /** 获取chartOption */
  getDefaultChartOption = (xAxis: string[], yAxis1: number[], yAxis2: number[]) => {
    const { chartData: { suffix, head, range }, colorPalette, chartStyle, chartHighLight } = this.props;
    const legendRange = head.slice(-2);
    const lastSuffix = suffix && suffix.slice(-2);
    const categoryAxisOption = {
      type: "category",
      offset: 2,
      splitLine: { show: false },
      axisTick: { show: false },
      axisPointer: {},
      axisLabel: { color: chartStyle.mainTextColor },
      axisLine: {
        lineStyle: {
          color: chartStyle.mainAxisColor,
        },
      },
      data: xAxis,
    };
    if (chartHighLight !== undefined) {
      categoryAxisOption.axisPointer = {
        show:  true,
        value: chartHighLight,
        type: "shadow",
        label: {
          show: false,
        },
        handle: {
          show: true,
          color: "rgba(255,255,255,0)",
        },
      };
    }
    const valueAxisOption = [
      {
        type: "value",
        max: Math.max(...[...yAxis1, ...range[2]]),
        axisLine: {
          lineStyle: {
            color: chartStyle.secondaryAxisColor,
          },
        },
        axisLabel: {
          textStyle: {
            fontSize: chartStyle.secondaryTextSize,
            color: chartStyle.secondaryTextColor,
          },
        },
        axisTick: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dotted",
            color: chartStyle.splitLineColor,
          },
        },
      },
      {
        type: "value",
        max: Math.max(...[...yAxis2, ...range[3]]),
        axisLine: {
          lineStyle: {
            color: chartStyle.secondaryAxisColor,
          },
        },
        axisLabel: {
          textStyle: {
            fontSize: chartStyle.secondaryTextSize,
            color: chartStyle.secondaryTextColor,
          },
        },
        axisTick: { show: false },
        splitLine: {
          show: false,
        },
      },
    ];

    return {
      xAxis: categoryAxisOption,
      yAxis: valueAxisOption,
      color: colorPalette.subColor,
      grid: {
        containLabel: true,
        borderWidth: 0,
        top: 28,
        bottom: 2,
        left: 2,
        right: 2,
        textStyle: {
          color: chartStyle.secondaryTextColor,
        },
      },
      itemStyle: {
        normal: {
          barBorderRadius: 3,
        },
      },
      legend: {
        show: false,
        top: "0",
        left: "center",
        icon: "circle",
        selectedMode: true,
        data: legendRange,
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: chartStyle.secondaryTextColor },
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: "rgba(0, 0, 0, 0.65)",
        textStyle: {
          align: "left",
          fontSize: 12,
          color: chartStyle.tooltipMainTextColor,
        },
        extraCssText: "box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.2)", // 添加阴影
        // tslint:disable-next-line: cyclomatic-complexity
        formatter(toolItems: any[]) {
          const filterData = toolItems;
          let result = `<div style="font-size: ${chartStyle.mainTextSize}px; color: ${chartStyle.tooltipSecondaryTextColor}">
            ${filterData[0].name}
          </div>`;
          for (let num = 0; num < filterData.length; num++) {
            const { seriesName, value } = filterData[num];
            result = result + `<div style="font-size: ${chartStyle.mainTextSize}px; color: ${chartStyle.tooltipSecondaryTextColor}">
              <span>${seriesName}：
              <span style="color: ${chartStyle.tooltipMainTextColor}">${value && value || "0"}</span>
              ${lastSuffix && lastSuffix[num]}</span>
            </div>`;
          }
          return result;
        },
      },
      series: [],
    };
  }
  /** 获取bar Option */
  getBarChartOption = (xAxis: string[], yAxis1: number[], yAxis2: number[]) => {
    const defaultChartOption = this.getDefaultChartOption(xAxis, yAxis1, yAxis2);
    const { chartData: { head }, colorPalette: { subColor }, barWidth, barGap } = this.props;
    const legendRange = head.slice(-2);
    const options = {
      ...defaultChartOption,
      series: [
        {
          barWidth,
          barGap,
          type: "bar",
          name: legendRange[0],
          stack: legendRange[0],
          barMinHeight: 1,
          itemStyle: {
            normal: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: `${tinycolor(subColor[0]).setAlpha(0.7).toRgbString()}` },
                  { offset: 1, color: `${tinycolor(subColor[0]).setAlpha(0.1).toRgbString()}` },
                ],
                global: false, // 缺省为 false
                /** tooltip的marker的颜色 */
                toString: () => subColor[0],
              },
            },
          },
          data: yAxis1.map((item: number, index: number) => {
            if (item < 0) {
              return{
                label: {
                  show: true,
                  formatter: (params: any) => { return "一"; },
                  fontSize: 2,
                  color: "transparent",
                  backgroundColor: subColor[0],
                  lineHeight: barWidth,
                  align: "center",
                  position: "bottom",
                  distance: 3,
                  borderRadius: 0,
                  rotate: -90,
                  offset: [barWidth / 2, 0],
                },
                value: item,
              };
            }
            return {
              label: {
                show: true,
                formatter: (params: any) => { return "一"; },
                fontSize: 2,
                color: "transparent",
                backgroundColor: subColor[0],
                lineHeight: barWidth,
                align: "center",
                position: "top",
                distance: 3,
                borderRadius: 0,
                rotate: 90,
                offset: [barWidth / 2, 0],
              },
              value: item,
            };
          }),
        },
        {
          barWidth,
          barGap,
          type: "bar",
          name: legendRange[1],
          stack: legendRange[1],
          barMinHeight: 3,
          yAxisIndex: 1,
          itemStyle: {
            normal: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: `${tinycolor(subColor[1]).setAlpha(0.7).toRgbString()}` },
                  { offset: 1, color: `${tinycolor(subColor[1]).setAlpha(0.1).toRgbString()}` },
                ],
                global: false, // 缺省为 false
                /** tooltip的marker的颜色 */
                toString: () => subColor[1],
              },
            },
          },
          data: yAxis2.map((item: number, index: number) => {
            if (item < 0) {
              return{
                label: {
                  show: true,
                  formatter: (params: any) => { return "一"; },
                  fontSize: 2,
                  color: "transparent",
                  backgroundColor: subColor[1],
                  lineHeight: barWidth,
                  align: "center",
                  position: "bottom",
                  distance: 3,
                  borderRadius: 0,
                  rotate: -90,
                  offset: [barWidth / 2, 0],
                },
                value: item,
              };
            }
            return {
              label: {
                show: true,
                formatter: (params: any) => { return "一"; },
                fontSize: 2,
                color: "transparent",
                backgroundColor: subColor[1],
                lineHeight: barWidth,
                align: "center",
                position: "top",
                distance: 3,
                borderRadius: 0,
                rotate: 90,
                offset: [barWidth / 2, 0],
              },
              value: item,
            };
          }),
        },
      ],
    };
    return options;
  }
}
